import { HttpEventType } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { Form, NgForm } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY } from 'rxjs';
import { catchError, filter, first, take } from 'rxjs/operators';
import { TimesheetSyncProvider, Uuid } from '../../../../../graphql';
import { BxDocumentSourceType } from '../../../../core/components/bx-documents/documents.model';
import { BxLogger } from '../../../../core/services/bx-logger.service';
import { DialogParams, DialogService } from '../../../../core/services/dialog.service';
import { EditTrackerService } from '../../../../core/services/edit-tracker.service';
import { AsideModalPosition, AsideModalSize, HelperService } from '../../../../core/services/helper.service';
import { PermissionService } from '../../../../core/services/permission';
import { StoresService } from '../../../../core/services/stores';
import { TaxService, TaxTypeEnum } from '../../../../data/logic/tax.service';
import { Job, JobInvoiceMethod, JobRetentionType } from '../../../../data/models';
import { IntegrationState } from '../../../../data/models/integrations.model';
import { RepoClientsService } from '../../../../data/repositories/repo-clients.service';
import { RepoCommonService } from '../../../../data/repositories/repo-common.service';
import { RepoIntegrationsService } from '../../../../data/repositories/repo-integrations.service';
import { RepoJobsService } from '../../../../data/repositories/repo-jobs.service';
import { RepoNotesService } from '../../../../data/repositories/repo-notes.service';
import { RepoStorageService, StorageContainer, StorageUploadFile } from '../../../../data/repositories/repo-storage.service';
import { SharedDataService } from '../../../../data/services/shared-data.service';
import { BuildingTypesService } from '../../../../shared/building-types/domain/api/building-types.api.service';
import { TimesheetApiService } from '../../../../shared/timesheets/domain/api/timesheet.api.service';
import { getAddressComponents } from '../../../../utils/helper-functions/address.functions';
import { setPhoneError } from '../../../../utils/helper-functions/phone-number-format-validator';
import { ClientsService } from '../../../clients';
import { LayoutService } from '../../../layout/layout.service';
import { TimesheetJob } from '../../accounting-import/timesheets/import-timesheets/import-timesheets.component';
import { JobCategoriesComponent } from '../../accounting-import/timesheets/job-locations-categories-side-form/job-categories-component';
import { JobsFeatureAccessService } from '../../jobs.feature-access.service';
import { JobVariationsService } from '../variations';
import { LaunchDarklyService } from './../../../../core/services/launchdarkly.service';
import { JobContractPriceModalComponent } from './contract-price-modal/job-contract-price-modal.component';
import { EditTaxRatesComponent } from './tax-rates-modal/edit-tax-rates.component';

type EditMode = keyof JobDetailsComponent['editModes'];
@Component({
    selector: 'app-job-details',
    templateUrl: './job-details.component.html',
    styleUrls: ['./job-details.component.scss'],
    standalone: false,
})
export class JobDetailsComponent implements OnInit {
    // @ts-ignore TS2564
    dateFormat: string;
    @Input() job!: Job & {
        TaxRatePercent: number;
        FranchiseFeePercent: number;
    };
    // @ts-ignore TS2564
    currentTaxRates: string;
    editModes = {
        job: false,
        customer: false,
        location: false,
    };
    includeRetention = false;
    numberInvalid = false;
    // @ts-ignore TS2564
    displayTaxProfile: string;
    // @ts-ignore TS2564
    buildingTypes: string[];
    retentionTypes = [
        {
            value: JobRetentionType.Excludingvariations,
            name: 'Excluding...',
        },
        {
            value: JobRetentionType.Includingvariations,
            name: 'Including...',
        },
    ];
    translations: any = {};
    invoiceMethods: any[] = [];
    documentsParams: any;
    showDeputyIntegrationsPanel = false;
    deputyLocationList: any;
    // @ts-ignore TS2564
    jobParentItems: any[];
    // @ts-ignore TS2564
    linkedDeputyAreas: any[];
    // @ts-ignore TS2564
    linkedDeputyAreaText: string;
    autocompleteOptions: any;
    // @ts-ignore TS2564
    worksLocationUrl: SafeResourceUrl;
    jobSource = BxDocumentSourceType.Job;

    permissions = {
        canEditJobDetails: false,
        viewDocuments: false,
        addDocuments: false,
        deleteDocuments: false,
        viewNotes: false,
        addNotes: false,
        deleteNotes: false,
    };

    digitalSignaturesEnabled = false;
    modulePermitted: boolean = true;

    constructor(
        private logger: BxLogger,
        private helperService: HelperService,
        private translateService: TranslateService,
        private clientsService: ClientsService,
        private sanitizer: DomSanitizer,
        private storesService: StoresService,
        private permissionService: PermissionService,
        private editTracker: EditTrackerService,
        private repoCommon: RepoCommonService,
        private repoNotes: RepoNotesService,
        private taxService: TaxService,
        public sharedData: SharedDataService,
        private repoJobs: RepoJobsService,
        private repoIntegrations: RepoIntegrationsService,
        private repoCustomers: RepoClientsService,
        private repoStorage: RepoStorageService,
        private dialogService: DialogService,
        private jobVariationsService: JobVariationsService,
        private readonly buildingTypesService: BuildingTypesService,
        private readonly launchDarklyService: LaunchDarklyService,
        private readonly timesheetApiService: TimesheetApiService,
        private readonly layoutService: LayoutService,
        private readonly jobsFeatureAccessService: JobsFeatureAccessService
    ) {
        translateService.get('general.dateFormat').subscribe((res) => {
            this.dateFormat = res;
        });
    }

    ngOnInit(): void {
        this.modulePermitted = this.jobsFeatureAccessService.canAccessJobDetails();
        if (!this.modulePermitted) return;
        this.includeRetention = this.shouldIncludeRetention();
        this.digitalSignaturesEnabled = this.launchDarklyService.get('digital-signatures');

        this.buildingTypesService
            .getBuildingTypes()
            .pipe(take(1))
            .subscribe((results) => {
                this.buildingTypes =
                    results.data.buildingTypes?.items?.map((type) => {
                        return type.description;
                    }) ?? [];
            });
        this.setTaxProfile();
        this.setInvoiceMethods();
        this.setTranslations();
        this.setDocumentsParams();
        this.loadWorksLocation(this.job.WorksLocationFormatted);
        this.loadDeputyIntegration();
        this.setPermissions();
    }

    shouldIncludeRetention() {
        return (
            (this.job.IsInvoiceMethodCompletionPercent || this.job.IsInvoiceMethodContractPrice) &&
            this.job.RetentionPaymentPercentage !== 0 &&
            this.job.RetentionPercentage !== 0
        );
    }

    loadDeputyIntegration() {
        this.repoIntegrations.Get().then((result) => {
            if (result && result.data && result.data.length) {
                // @ts-ignore TS7006
                result.data.map((r) => {
                    if (r.name == 'Deputy' && r.status.state !== IntegrationState.NotConnected) {
                        this.showDeputyIntegrationsPanel = true;
                        this.getDeputyLocationList();
                        this.getLinkedDeputyAreasCount();
                    }
                });
            }
        });
    }

    setDocumentsParams() {
        this.documentsParams = {
            reference: this.job.ID_Job,
            sourceType: 'Job',
            leadId: this.job.ID_Lead,
            references: [{ Id: this.job.ID_Job, Type: 'Job' }],
        };
        if (this.job.ID_Estimate) {
            this.documentsParams.references.push({ Id: this.job.ID_Estimate, Type: 'Estimate' });
        }
        if (this.job.ID_Lead) {
            this.documentsParams.references.push({ Id: this.job.ID_Lead, Type: 'Lead' });
        }
    }

    setInvoiceMethods() {
        this.invoiceMethods = this.repoJobs.InvoiceMethods;
    }

    getInvoiceMethodName() {
        return this.invoiceMethods.find((i) => {
            return i.value === this.job.InvoiceMethod;
        })?.name;
    }

    setTranslations() {
        this.translations = {};
        const text = this.translateService.instant(['general.variations', 'general.invoice.contract-price']);
        this.translations.variations = text['general.variations'];
        this.translations.defaultInvoiceMethodName = text['general.invoice.contract-price'];
        this.invoiceMethods[0].name = this.translations.defaultInvoiceMethodName;
        // @ts-ignore TS2532
        this.retentionTypes[0].name = `Excluding ${this.translations.variations}`;
        // @ts-ignore TS2532
        this.retentionTypes[1].name = `Including ${this.translations.variations}`;
    }

    setTaxProfile() {
        this.displayTaxProfile = this.job.TaxRatesDescription;
        if (!this.taxService.matchTaxProfile(this.job, this.sharedData.Common.TaxProfiles)) this.displayTaxProfile += ' (Adjusted)';
    }

    startEditing(form: Form, editMode: string) {
        this.currentTaxRates = this.job.TaxRates;
        // @ts-ignore TS7053
        this.editModes[editMode] = true;
        this.editTracker.start(form, false);
    }

    stopEditing(editMode: EditMode) {
        this.editModes[editMode] = false;
        this.editTracker.stop();
    }

    cancelForm(mode: EditMode) {
        this.cancelChanges();
        this.stopEditing(mode);
    }

    changeInvoiceMethod() {
        this.includeRetention = this.shouldIncludeRetention();

        if (!this.includeRetention) {
            this.job.RetentionPercentage = 0;
            this.job.RetentionType = JobRetentionType.Excludingvariations;
            this.job.RetentionPaymentPercentage = 0;
        }
    }

    saveDetails() {
        const oldNumber = this.job.entityAspect.originalValues['Number'];
        const newNumber = this.job.Number;

        if (!this.includeRetention) {
            this.job.RetentionPercentage = 0;
            this.job.RetentionType = JobRetentionType.Excludingvariations;
            this.job.RetentionPaymentPercentage = 0;
        }

        if (oldNumber && oldNumber !== newNumber) {
            this.repoCommon
                .CheckNumberExists(newNumber)
                .then((response) => {
                    if (response.results[0]) {
                        // set validation error on number
                        this.numberInvalid = true;
                    } else {
                        this.numberInvalid = false;
                        this.saveDetailsTaxRates(() => {
                            // record a note about the change
                            this.repoNotes
                                .post(this.job.ID_Job, {
                                    source: 'Jobs',
                                    noteText: "Number changed from '" + oldNumber + "' to '" + newNumber + "'",
                                })
                                .pipe(first())
                                .subscribe();
                            this.stopEditing('job');
                        });
                    }
                })
                .catch((error) => {
                    this.logger.error('Cannot save changes now.');
                    this.cancelChanges();
                    this.stopEditing('job');
                });
        } else {
            this.numberInvalid = false;
            this.saveDetailsTaxRates();
            this.stopEditing('job');
        }
    }

    cancelChanges() {
        this.numberInvalid = false;
        this.job.entityAspect.rejectChanges();
        if (this.job.CalculationMethod >= 1) this.setTaxProfile();
    }

    saveDetailsTaxRates(callback?: () => void) {
        // If using the TaxRates calculation method and those rates have changed...
        if (this.job.CalculationMethod >= 1 && this.currentTaxRates !== this.job.TaxRates) {
            const params: DialogParams = {
                type: 'warning',
                title: 'Tax rates have changed',
                text: 'Tax rates of applicable items will be reset to the newly updated rates, resulting in any customizations being lost. Confirm you want to continue?',
                okText: 'Confirm',
                cancelText: 'Cancel',
            };

            this.dialogService.show(params).then(
                () => {
                    this.updateJobItemsTax();
                    this.updatePurchaseOrdersTax();
                    this.updateJobVariationsTax();
                    this.updateJobPaymentsTax();

                    if (typeof callback === 'function') callback();
                    this.saveChanges();
                    this.setTaxProfile();
                },
                () => this.cancelChanges()
            );
        } else {
            if (typeof callback === 'function') callback();
            this.saveChanges();
        }
    }

    updateJobItemsTax() {
        // @ts-ignore TS2532
        this.job.JobItems.forEach((i) => {
            if (i.ParentId && !i.IsDeleted) {
                i.TaxRates = this.taxService.resetItemTaxCodes(this.job.TaxRates, TaxTypeEnum.Expense, i.CostItemType);
            }
        });
        this.job.calculateTotals();
    }

    hasInvoices() {
        return this.numberNonDeletedInvoices() > 0;
    }

    numberNonDeletedInvoices() {
        if (this.job === null) {
            return 0;
        } else {
            if (this.job.JobPayments === null) {
                return 0;
            } else {
                // @ts-ignore TS2532
                return this.job.JobPayments.filter((jobpayment) => !jobpayment.IsDeleted).length;
            }
        }
    }

    updatePurchaseOrdersTax() {
        // @ts-ignore TS2532
        this.job.PurchaseOrders.forEach((p) => {
            if (!p.IsDeleted && p.OrderStatus === 'Unsent') {
                p.TaxRatesDescription = this.job.TaxRatesDescription;
                p.TaxRatesShortCode = this.job.TaxRatesShortCode;
                p.TaxRates = this.job.TaxRates;
                p.DeliveryTaxRates = this.taxService.resetItemTaxCodes(p.TaxRates, TaxTypeEnum.Expense, 1);
                p.PurchaseOrderItems.forEach((i) => {
                    i.TaxRates = this.taxService.resetItemTaxCodes(p.TaxRates, TaxTypeEnum.Expense, i.CostItemType);
                });
                p.calculateTotal();
            }
        });
    }

    updateJobVariationsTax() {
        // @ts-ignore TS2532
        this.job.JobVariations.forEach((v) => {
            if (!v.IsDeleted && v.Status === 'Pending') {
                v.TaxRatesDescription = this.job.TaxRatesDescription;
                v.TaxRatesShortCode = this.job.TaxRatesShortCode;
                v.TaxRates = this.job.TaxRates;
                // @ts-ignore TS2532
                v.JobVariationTasks.forEach((t) => {
                    t.TaxRates = this.taxService.resetItemTaxCodes(v.TaxRates, TaxTypeEnum.Income, t.CostItemType);
                });
                v.calculateTotal();
            }
        });
    }

    updateJobPaymentsTax() {
        // @ts-ignore TS2532
        this.job.JobPayments.forEach((i) => {
            if (!i.IsDeleted && i.Status === 'Pending') {
                i.TaxRatesDescription = this.job.TaxRatesDescription;
                i.TaxRatesShortCode = this.job.TaxRatesShortCode;
                i.TaxRates = this.job.TaxRates;

                if (i.IsDepositInvoice) {
                    i.DepositTaxRates = this.taxService.resetItemTaxCodes(i.TaxRates, TaxTypeEnum.Income, 1);
                }

                if (this.job.IsInvoiceMethodCostPlus) {
                    i.JobPaymentPurchaseOrderItems.forEach((p) => {
                        p.TaxRates = this.taxService.resetItemTaxCodes(i.TaxRates, TaxTypeEnum.Income, p.CostItemType);
                    });
                }

                i.calculateTotal();
            }
        });
    }

    saveChanges() {
        return this.repoJobs.Save(null, 'Saved');
    }

    getDeputyLocationList() {
        this.timesheetApiService
            .getTimesheetLocations({ jobId: this.job.ID_Job, timesheetProvider: TimesheetSyncProvider.Deputy })
            .pipe(
                first(),
                catchError((error) => {
                    this.logger.error(error);
                    return EMPTY;
                })
            )
            .subscribe((response) => {
                if (response.data) {
                    this.deputyLocationList =
                        response.data.timesheetLocations?.map((location) => {
                            return {
                                deputyId: location.id,
                                deputyLocation: location.address,
                            };
                        }) ?? [];
                } else {
                    this.logger.error('Error getting Deputy locations');
                }
            });
    }

    // @ts-ignore TS7006
    setDeputyLocationID(location) {
        this.job.TimesheetLocationId = location;
        this.repoJobs.Save(null, 'Deputy location set').then(() => {
            this.getDeputyLocationList();
        });
    }

    // @ts-ignore TS7006
    clearDeputyLocationID(evt) {
        evt.stopPropagation();
        const params = {
            title: 'Remove link with Deputy?',
            text: 'Confirm you want to remove the link to Deputy for this job and all areas.',
        };
        this.dialogService.show(params).then(
            () => {
                // @ts-ignore TS2322
                this.job.TimesheetLocationId = null;
                this.clearDeputyAreaReferences();
                this.repoJobs.Save(null, 'Deputy location link cleared');
            },
            () => ({})
        );
    }

    clearDeputyAreaReferences() {
        // @ts-ignore TS2532
        this.jobParentItems = this.job.JobItems.filter((i) => {
            return !i.IsDeleted && i.ParentId === null;
        });
        this.jobParentItems.forEach((parent) => {
            parent.TimesheetCategoryId = null;
        });
        this.linkedDeputyAreas = [];
        this.linkedDeputyAreaText = '';
    }

    createDeputyLocation() {
        const params: DialogParams = {
            title: 'Link job to Deputy?',
            text: 'Confirm you want to create a new location in Deputy (for the work address) and link it to this job.',
            type: 'success',
        };

        this.dialogService.show(params).then(() => {
            this.timesheetApiService
                .createTimesheetLocation({ jobId: this.job.ID_Job, timesheetProvider: TimesheetSyncProvider.Deputy })
                .pipe(
                    first(),
                    catchError((error) => {
                        this.logger.error(error);
                        return EMPTY;
                    })
                )
                .subscribe((response) => {
                    const idStr = response?.data?.createTimesheetLocation?.timesheetLocation?.id;
                    if (!idStr) return this.logger.error('Error adding location to Deputy. Location may already exist.');

                    this.setDeputyLocationID(idStr);
                    this.logger.success('Deputy location added');
                });
        });
    }

    getLinkedDeputyAreasCount() {
        this.linkedDeputyAreas = [];
        // @ts-ignore TS2532
        if (this.job.JobItems.length > 0) {
            // @ts-ignore TS2532
            this.linkedDeputyAreas = this.job.JobItems.filter((i) => {
                return i.TimesheetCategoryId && !i.ParentId && !i.IsDeleted;
            });
        }
        this.linkedDeputyAreaText = '';
        this.linkedDeputyAreaText = `(${this.linkedDeputyAreas.length} area${
            this.linkedDeputyAreas.length > 1 || this.linkedDeputyAreas.length === 0 ? 's' : ''
        } linked)`;
    }

    setupDeputyAreas() {
        const initState = {
            selectedJob: {
                iD_Job: this.job.ID_Job as Uuid,
                description: this.job.Description,
                number: this.job.Number,
                timesheetLocationId: this.job.TimesheetLocationId,
            } satisfies TimesheetJob,
            timesheetProvider: TimesheetSyncProvider.Deputy,
            isModal: true,
        };

        const modalRef = this.helperService.showModalAside(
            JobCategoriesComponent,
            {
                initialState: initState,
            },
            this.layoutService.isMobile() ? AsideModalSize.wide : AsideModalSize.medium,
            AsideModalPosition.left
        );

        modalRef.content!.action.pipe(take(1)).subscribe((count) => {
            this.linkedDeputyAreaText = `(${count} area${count > 1 || count === 0 ? 's' : ''} linked)`;
        });
    }

    changeContractPrice() {
        const initialState = {
            job: this.job,
        };
        const modalRef = this.helperService.showModal(JobContractPriceModalComponent, { initialState });
        // @ts-ignore TS2532
        modalRef.content.action.pipe(take(1)).subscribe((result) => {
            if (!result.cancelled) {
                if (result.totalIncTax !== this.job.ContractTotalIncTax) {
                    const reason = `${this.translations.defaultInvoiceMethodName} changed from $${this.job.ContractTotalIncTax.toFixed(
                        2
                        // @ts-ignore TS2532
                    )} to $${result.totalIncTax.toFixed(2)}: ${result.reason}`;
                    // @ts-ignore TS2322
                    this.job.ContractTotal = result.total;
                    // @ts-ignore TS2322
                    this.job.ContractTax = result.tax;
                    // @ts-ignore TS2322
                    this.job.ContractTotalIncTax = result.totalIncTax;
                    // @ts-ignore TS2322
                    this.job.ContractMarkup = result.markup;
                    this.repoNotes
                        .post(this.job.ID_Job, {
                            source: 'Jobs',
                            noteText: reason,
                        })
                        .pipe(first())
                        .subscribe();
                    this.job.updateInvoicePercentages();
                    this.saveChanges();
                }
            }
        });
    }

    // @ts-ignore TS7006
    isInvoiceMethodAvailable(methodVal) {
        // Cannot use Completion Percent when no estimate exists
        if (methodVal === 2 && this.job.ID_Estimate == null) return false;
        return true;
    }

    isCompletionPercentForOldEstimate() {
        return (
            this.job.Estimate && this.job.Estimate.CalculationMethod === 0 && this.job.InvoiceMethod === JobInvoiceMethod.CompletionPercent
        );
    }

    colorChanged(color: string) {
        this.job.ColorCode = color;
    }

    // @ts-ignore TS7006
    validatePhoneError(field: string, form: NgForm, e) {
        setPhoneError(field, form, e);
    }

    getEncodedAddress(address: string) {
        return encodeURIComponent(address);
    }

    handleAddressChange(event: google.maps.places.Place) {
        const addressComponents = getAddressComponents(event);
        const state = this.sharedData.Common.StatesInfo.find(
            // @ts-ignore TS7006
            (s) => s.code === addressComponents.stateName || s.name === addressComponents.stateName
        );
        // @ts-ignore TS2322
        this.job.ClientAddress1 = addressComponents.addressLine1;
        this.job.ClientCityTown = addressComponents.city;
        this.job.ClientPostCode = addressComponents.postcode;
        this.job.ClientState = state ? state.value : '';
    }

    handleWorksLocationAddressChange(event: google.maps.places.Place) {
        const addressComponents = getAddressComponents(event);
        const state = this.sharedData.Common.StatesInfo.find(
            // @ts-ignore TS7006
            (s) => s.code === addressComponents.stateName || s.name === addressComponents.stateName
        );
        // @ts-ignore TS2322
        this.job.WorksLocationAddress = addressComponents.addressLine1;
        this.job.WorksLocationSuburb = addressComponents.city;
        this.job.WorksLocationPostcode = addressComponents.postcode;
        this.job.WorksLocationState = state ? state.value : '';
        this.job.WorksLocationLat = addressComponents?.location?.lat;
        this.job.WorksLocationLng = addressComponents?.location?.lng;
        this.loadWorksLocation(this.job.WorksLocationFormatted);
    }

    // @ts-ignore TS7006
    loadWorksLocation(val) {
        const address = val ? val : this.sharedData.UserData.tenant.BillingAddressFull,
            url = this.repoCommon.GetEmbedMapUrl(address);
        this.worksLocationUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }

    updateCustomerAddress(customer: any) {
        if (customer) {
            this.job.ClientAddress1 = customer.Address;
            this.job.ClientCityTown = customer.CityTown;
            this.job.ClientState = customer.State;
            this.job.ClientPostCode = customer.PostCode;
            this.job.ClientPhone = customer.Phone;
            this.job.ClientMobile = customer.Mobile;
            this.job.ClientEmail = customer.Email;
        }
    }

    saveCustomer(form: NgForm) {
        if (this.job.ClientName) {
            this.repoCustomers.GetByName(this.job.ClientName).then((customer) => {
                if (!customer) {
                    const params: DialogParams = {
                        title: 'Create Client?',
                        text: `'${this.job.ClientName}' does not exist as a client. Would you like to save as a client?`,
                        type: 'warning',
                        okText: 'Yes',
                        cancelText: 'No thanks',
                    };
                    this.dialogService.show(params).then(
                        () => this.createCustomerAndSave(),
                        () => this.syncEstimateCustomerAndSave()
                    );
                } else {
                    if (this.job.ID_Customer !== customer.ID_Customer) {
                        this.job.ID_Customer = customer.ID_Customer;
                    }

                    customer.Address = this.job.ClientAddress1;
                    customer.CityTown = this.job.ClientCityTown;
                    customer.State = this.job.ClientState;
                    customer.PostCode = this.job.ClientPostCode;
                    customer.Phone = this.job.ClientPhone;
                    customer.Mobile = this.job.ClientMobile;
                    customer.Email = this.job.ClientEmail;
                    const customerModified = customer.entityAspect.entityState.isModified();
                    this.syncEstimateCustomerAndSave().then(() => {
                        if (customerModified) {
                            this.repoCustomers.Update(customer.ID_Customer);
                        }
                    });
                }
            });
        }
        this.stopEditing('customer');
    }

    createCustomerAndSave() {
        this.clientsService
            .newCustomer({
                Name: this.job.ClientName,
                Address: this.job.ClientAddress1,
                CityTown: this.job.ClientCityTown,
                State: this.job.ClientState,
                PostCode: this.job.ClientPostCode,
                Phone: this.job.ClientPhone,
                Email: this.job.ClientEmail,
                Mobile: this.job.ClientMobile,
                CustomerType: 'Person',
                preventOpen: true,
            })
            .then(
                (customer) => {
                    this.job.ID_Customer = customer.ID_Customer;
                    this.syncEstimateCustomerAndSave();
                },
                () => this.syncEstimateCustomerAndSave()
            );
    }

    syncEstimateCustomerAndSave() {
        if (this.job.ID_Estimate && this.job.Estimate && this.job.ID_Customer) {
            this.job.Estimate.ID_Customer = this.job.ID_Customer;
            this.job.Estimate.Name = this.job.ClientName;
            this.job.Estimate.Address = this.job.ClientAddress1;
            this.job.Estimate.Suburb = this.job.ClientCityTown;
            this.job.Estimate.State = this.job.ClientState;
            this.job.Estimate.PostCode = this.job.ClientPostCode;
            this.job.Estimate.Mobile = this.job.ClientMobile;
            this.job.Estimate.Phone = this.job.ClientPhone;
            this.job.Estimate.Email = this.job.ClientEmail;

            return this.saveChanges();
        } else return this.saveChanges();
    }

    saveWorksLocation(form: NgForm) {
        if (this.job.WorksLocationFormatted) {
            this.repoCommon
                .GetStaticMap({
                    address: this.job.WorksLocationFormatted,
                })
                .then((result) => {
                    const blob = result.data;
                    var f = new File([blob], 'WorkLocationMap.png', { type: 'image/png' });
                    this.repoStorage
                        .upload([f], { reference: this.job.ID_Job, container: StorageContainer.documents })
                        .pipe(
                            filter((e) => e.type === HttpEventType.Response),
                            take(1)
                        )
                        .subscribe((event) => {
                            if (event.type === HttpEventType.Response) {
                                const uploadedFiles = event.body as StorageUploadFile[];
                                const uploadedFile = uploadedFiles[0];
                                if (uploadedFile) {
                                    this.storesService.updateDocWidget();
                                    this.job.WorksLocationMapUrl = uploadedFile.fileUrl;
                                    this.saveChanges();
                                    this.stopEditing('location');
                                }
                            }
                        });
                });
        } else {
            // @ts-ignore TS2322
            this.job.WorksLocationMapUrl = null;
            this.saveChanges();
            this.stopEditing('location');
        }
    }

    editTaxRates() {
        const editDetails = {
            description: this.job.TaxRatesDescription,
            shortCode: this.job.TaxRatesShortCode,
            taxRates: this.job.TaxRates,
        };
        const config = {
            details: editDetails,
            alterable: true,
        };
        const modalRef = this.helperService.showModalAside(EditTaxRatesComponent, { initialState: config }, AsideModalSize.medium);
        // @ts-ignore TS2532
        modalRef.content.action.subscribe((result) => {
            if (!result.cancelled) {
                this.job.TaxRatesDescription = result.details.description;
                this.job.TaxRatesShortCode = result.details.shortCode;
                this.job.TaxRates = result.details.taxRates;
                this.setTaxProfile();
            }
        });
    }

    private setPermissions() {
        this.permissionService
            .getStates([
                'jobs.maintain-jobs.edit-job-details',
                'jobs.maintain-job-documents.view-documents',
                'jobs.maintain-job-documents.add-documents',
                'jobs.maintain-job-documents.delete-documents',
                'jobs.maintain-job-notes.view-notes',
                'jobs.maintain-job-notes.add-notes',
                'jobs.maintain-job-notes.delete-notes',
            ])
            .subscribe((states) => {
                this.permissions = {
                    // @ts-ignore TS2322
                    canEditJobDetails: states['jobs.maintain-jobs.edit-job-details'],
                    // @ts-ignore TS2322
                    viewDocuments: states['jobs.maintain-job-documents.view-documents'],
                    // @ts-ignore TS2322
                    addDocuments: states['jobs.maintain-job-documents.add-documents'],
                    // @ts-ignore TS2322
                    deleteDocuments: states['jobs.maintain-job-documents.delete-documents'],
                    // @ts-ignore TS2322
                    viewNotes: states['jobs.maintain-job-notes.view-notes'],
                    // @ts-ignore TS2322
                    addNotes: states['jobs.maintain-job-notes.add-notes'],
                    // @ts-ignore TS2322
                    deleteNotes: states['jobs.maintain-job-notes.delete-notes'],
                };
            });
    }

    openJobVariation(variationId: Uuid) {
        this.jobVariationsService.viewVariationById({ variationId, jobId: this.job.ID_Job });
    }
}
